import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import { Icon, type LucideIcon } from '../icons/Icon';

const Wrap = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '1', minWidth: 0 },
});

const Label = chakra('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.5',
    fontFamily: 'body',
    fontSize: 'xs',
    fontWeight: 'medium',
    color: 'text.muted',
    letterSpacing: 'wide',
  },
});

const Value = chakra('span', {
  base: {
    fontFamily: 'heading',
    fontWeight: 'bold',
    color: 'text.primary',
    lineHeight: 'tight',
    letterSpacing: 'tight',
    fontVariantNumeric: 'tabular-nums',
  },
});

const Hint = chakra('span', {
  base: { fontFamily: 'body', fontSize: 'xs', color: 'text.secondary' },
});

type MetricSize = 'sm' | 'md' | 'lg';

const valueSize: Record<MetricSize, HTMLChakraProps<'span'>> = {
  sm: { fontSize: 'lg' },
  md: { fontSize: '2xl' },
  lg: { fontSize: '3xl' },
};

export interface MetricProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  label: ReactNode;
  value: ReactNode;
  /** Small leading glyph on the label. */
  icon?: LucideIcon;
  /** Muted supporting line under the value. */
  hint?: ReactNode;
  size?: MetricSize;
  /** Testid applied to the value (for e2e / assertions). */
  valueTestId?: string;
}

/** A single labelled statistic — cost, tokens, duration. Editorial value, quiet label. */
export const Metric = forwardRef<HTMLDivElement, MetricProps>(function Metric(
  { label, value, icon, hint, size = 'md', valueTestId, ...rest },
  ref,
) {
  return (
    <Wrap ref={ref} {...rest}>
      <Label>
        {icon && <Icon as={icon} size="xs" tone="muted" />}
        {label}
      </Label>
      <Value data-testid={valueTestId} {...valueSize[size]}>
        {value}
      </Value>
      {hint && <Hint>{hint}</Hint>}
    </Wrap>
  );
});
